import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Alert,
  CircularProgress,
  Chip,
  alpha,
} from '@mui/material';
import {
  Email,
  Phone,
  AccessTime,
  Person,
  TrendingUp,
  CalendarToday,
  Storefront,
  WorkOutline,
} from '@mui/icons-material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { apiService } from '../../services/apiService';
import { colors } from '../../theme/theme';
import { StudioContactInquiry } from './types';
import {
  StatCard,
  SearchBar,
  DATA_GRID_SX,
  tableCardSx,
  formatSubmittedDate,
  STAT_CARDS_GRID_SX,
  DATA_GRID_CONTAINER_SX,
  isThisMonth,
  fetchAllPages,
} from './shared';

export function StudioContactTab() {
  const [inquiries, setInquiries] = useState<StudioContactInquiry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchInquiries();
  }, []);

  const fetchInquiries = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const allInquiries = await fetchAllPages<StudioContactInquiry>(async (page, pageSize) => {
        const r = await apiService.getStudioContactInquiries({ page, pageSize });
        return { total: r.total || 0, items: r.inquiries || [] };
      });

      allInquiries.sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
      setInquiries(allInquiries);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to fetch studio contact inquiries');
    } finally {
      setIsLoading(false);
    }
  };

  const filteredInquiries = useMemo(() => {
    const searchLower = searchTerm.toLowerCase().trim();
    return inquiries.filter(
      (inquiry) =>
        searchLower === '' ||
        inquiry.contactName?.toLowerCase().includes(searchLower) ||
        inquiry.studioName?.toLowerCase().includes(searchLower) ||
        inquiry.email?.toLowerCase().includes(searchLower) ||
        inquiry.phoneNumber?.toLowerCase().includes(searchLower) ||
        inquiry.interestedTier?.toLowerCase().includes(searchLower)
    );
  }, [inquiries, searchTerm]);

  const columns: GridColDef[] = [
    {
      field: 'contactName',
      headerName: 'Contact Name',
      width: 200,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap={1.5} sx={{ py: 1 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              backgroundColor: alpha(colors.primary, 0.1),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Person sx={{ fontSize: 20, color: colors.primary }} />
          </Box>
          <Typography variant="body2" fontWeight="600" color={colors.textPrimary}>
            {params.value || 'N/A'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'studioName',
      headerName: 'Studio',
      width: 200,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap={1.5} sx={{ py: 1 }}>
          <Storefront sx={{ fontSize: 18, color: colors.warning }} />
          <Typography variant="body2" color={colors.textPrimary} fontWeight="500">
            {params.value || 'N/A'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'email',
      headerName: 'Email',
      flex: 1,
      minWidth: 220,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap={1.5} sx={{ py: 1 }}>
          <Email sx={{ fontSize: 18, color: colors.info }} />
          <Typography variant="body2" color={colors.textPrimary}>
            {params.value || 'N/A'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'phoneNumber',
      headerName: 'Phone',
      width: 160,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap={1.5} sx={{ py: 1 }}>
          <Phone sx={{ fontSize: 18, color: colors.success }} />
          <Typography variant="body2" color={colors.textPrimary} fontWeight="500">
            {params.value || 'N/A'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'interestedTier',
      headerName: 'Interested In',
      width: 180,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap={1.5} sx={{ py: 1 }}>
          <WorkOutline sx={{ fontSize: 18, color: colors.primary }} />
          <Chip
            label={params.value || 'N/A'}
            size="small"
            sx={{
              backgroundColor: alpha(colors.primary, 0.1),
              color: colors.primary,
              fontWeight: 600,
              fontSize: '0.75rem',
            }}
          />
        </Box>
      ),
    },
    {
      field: 'createdAt',
      headerName: 'Submitted On',
      width: 180,
      renderCell: (params) => {
        const { date, time } = formatSubmittedDate(params.value);
        return (
          <Box display="flex" alignItems="center" gap={1} sx={{ py: 1 }}>
            <AccessTime sx={{ fontSize: 18, color: colors.textSecondary }} />
            <Box>
              <Typography variant="body2" fontWeight="bold">
                {date}
              </Typography>
              <Typography variant="caption" color={colors.textSecondary} display="block">
                {time}
              </Typography>
            </Box>
          </Box>
        );
      },
    },
  ];

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  const thisMonth = inquiries.filter((i) => isThisMonth(i.createdAt)).length;
  const uniqueStudios = new Set(inquiries.map((i) => i.studioName?.toLowerCase())).size;

  return (
    <Box sx={{ width: '100%' }}>
      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={STAT_CARDS_GRID_SX}>
        <StatCard
          label="Total Inquiries"
          value={inquiries.length}
          icon={<TrendingUp sx={{ fontSize: 28, color: colors.primary }} />}
          color={colors.primary}
        />
        <StatCard
          label="This Month"
          value={thisMonth}
          icon={<CalendarToday sx={{ fontSize: 28, color: colors.info }} />}
          color={colors.info}
        />
        <StatCard
          label="Studios"
          value={uniqueStudios}
          icon={<Storefront sx={{ fontSize: 28, color: colors.warning }} />}
          color={colors.warning}
        />
      </Box>

      <SearchBar
        value={searchTerm}
        onChange={setSearchTerm}
        placeholder="Search by name, studio, email, phone, or tier..."
      />

      <Card sx={tableCardSx}>
        <CardContent sx={{ p: 0 }}>
          <Box sx={DATA_GRID_CONTAINER_SX}>
            <DataGrid
              rows={filteredInquiries}
              columns={columns}
              getRowId={(row) => row._id}
              pageSizeOptions={[10, 25, 50, 100]}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              disableRowSelectionOnClick
              getRowHeight={() => 75}
              sx={DATA_GRID_SX}
            />
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
